import { useTokenStore } from '@/lib/store'
import { trackException } from '@/lib/telemetry'
import axios, { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

// Module augmentation for enriched error properties
declare module 'axios' {
  interface AxiosError {
    userMessage?: string
    /** Set only when the message was authored by the backend (vs a status-code template) */
    backendMessage?: string
    technicalDetail?: string
  }
  // On AxiosRequestConfig too so callers can pass it per-request:
  // true marks a non-idempotent request safe to retry; false opts out entirely.
  interface AxiosRequestConfig {
    _retryable?: boolean
  }
  interface InternalAxiosRequestConfig {
    _retryCount?: number
    _retryable?: boolean
    _401Retried?: boolean
  }
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request could not be processed. Please verify your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource could not be found.',
  408: 'The server took too long to respond. Please try again.',
  409: 'A conflict occurred. Another user may have modified this record. Please refresh and try again.',
  412: 'This record has been modified by another user. Please refresh and try again.',
  422: 'The submitted data is invalid. Please review your input.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An internal server error occurred. Please try again later.',
  502: 'The service is temporarily unavailable. Please try again shortly.',
  503: 'The service is currently undergoing maintenance. Please try again later.',
  504: 'The server did not respond in time. Please try again.',
}

const NETWORK_ERROR_MESSAGE = 'A network error occurred. Please check your connection and try again.'
const UNKNOWN_ERROR_MESSAGE = 'An unexpected error occurred. Please try again later.'

const MAX_RETRIES = 3
const MAX_BACKOFF_MS = 10_000
const MAX_RETRY_AFTER_MS = 30_000
const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504])
const IDEMPOTENT_METHODS = new Set(['get', 'delete', 'head', 'options'])

function extractBackendMessage(error: AxiosError): string | undefined {
  const data = error.response?.data as Record<string, unknown> | string | undefined
  if (!data) return undefined

  if (typeof data === 'string') {
    const text = data.trim()
    // A markup body (e.g. a proxy's HTML error page) is not a user-facing message
    if (!text || text.startsWith('<')) return undefined
    return text
  }
  if (typeof data === 'object') {
    // FastAPI HTTPException detail
    if ('detail' in data && typeof data.detail === 'string') return data.detail
    if ('detail' in data && data.detail && typeof data.detail === 'object') {
      const detail = data.detail as Record<string, unknown>
      const formatted = formatProblemDetail(detail)
      if (formatted) return formatted
    }
    if (typeof data.error === 'string') return data.error
    if (typeof data.message === 'string') return data.message
    // Dynamics 365 API error
    const dynamicsError = data.error as Record<string, unknown> | undefined
    if (dynamicsError && typeof dynamicsError.message === 'string') return dynamicsError.message
  }
  return undefined
}

function formatProblemDetail(detail: Record<string, unknown>): string | undefined {
  const message = readString(detail.message)
  const dtools = readRecord(detail.dtools)
  const dtoolsMessage = readString(dtools?.message)
  const dtoolsDetail = readString(dtools?.message_detail) ?? readString(dtools?.raw)
  const lines = [message].filter(Boolean) as string[]

  if (dtoolsMessage && dtoolsMessage !== message) lines.push(`D-Tools: ${dtoolsMessage}`)
  if (dtoolsDetail && dtoolsDetail !== dtoolsMessage) lines.push(dtoolsDetail)

  const context = [
    contextPart('Status', detail.status_code),
    contextPart('Endpoint', dtools?.path),
    contextPart('Quote', detail.quote_number ?? detail.quote_id),
    contextPart('SI project', detail.si_project_number ?? detail.si_project_id),
    contextPart('Branch', detail.branch_number),
    contextPart('Sync log', detail.sync_log_id),
  ].filter(Boolean)
  if (context.length > 0) lines.push(context.join(' | '))
  if (typeof detail.retryable === 'boolean') lines.push(`Retryable: ${detail.retryable ? 'yes' : 'no'}`)

  return lines.length > 0 ? lines.join('\n') : undefined
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function contextPart(label: string, value: unknown): string | undefined {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : undefined
  return text ? `${label}: ${text}` : undefined
}

function extractTechnicalDetail(error: AxiosError): string | undefined {
  const backendMessage = extractBackendMessage(error)
  if (backendMessage) return backendMessage
  const data = error.response?.data
  if (data && typeof data === 'object') return JSON.stringify(data).slice(0, 1000)
  return error.message
}

function enrichError(error: AxiosError): void {
  error.technicalDetail = extractTechnicalDetail(error)

  // Prefer an explicit message from the backend (FastAPI detail, Dynamics error.message,
  // or raw string body) — these are authored by our API for end-users. Fall back to a
  // generic status-code template only when the backend gave us nothing useful.
  const backendMessage = extractBackendMessage(error)
  const status = error.response?.status

  if (backendMessage) {
    error.backendMessage = backendMessage
    error.userMessage = backendMessage
  } else if (status && STATUS_MESSAGES[status]) {
    error.userMessage = STATUS_MESSAGES[status]
  } else if (!error.response) {
    error.userMessage = NETWORK_ERROR_MESSAGE
  } else {
    error.userMessage = UNKNOWN_ERROR_MESSAGE
  }
}

function shouldRetry(error: AxiosError, config: InternalAxiosRequestConfig): boolean {
  // Explicit opt-out — callers that manage retries themselves (e.g. query-level
  // retry) set _retryable: false so the two layers don't multiply.
  if (config._retryable === false) return false

  const retryCount = config._retryCount ?? 0
  if (retryCount >= MAX_RETRIES) return false

  const method = (config.method ?? '').toLowerCase()
  const isIdempotent = IDEMPOTENT_METHODS.has(method)
  if (!isIdempotent && !config._retryable) return false

  const status = error.response?.status
  if (!status) return true // Network error — always retry idempotent/marked-safe
  return RETRYABLE_STATUSES.has(status)
}

function getBackoffDelay(error: AxiosError, attempt: number): number {
  // Respect Retry-After header for 429
  if (error.response?.status === 429) {
    const retryAfter = error.response.headers?.['retry-after']
    if (retryAfter) {
      const seconds = Number(retryAfter)
      if (!isNaN(seconds)) {
        return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS)
      }
    }
  }
  return Math.min(1000 * Math.pow(2, attempt), MAX_BACKOFF_MS)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

let refreshPromise: Promise<string> | null = null

async function handle401Retry(error: AxiosError, instance: AxiosInstance): Promise<AxiosResponse | null> {
  const config = error.config
  if (!config || error.response?.status !== 401 || config._401Retried) return null

  config._401Retried = true

  try {
    // Share a single refresh promise across concurrent 401s
    if (!refreshPromise) {
      refreshPromise = (async () => {
        useTokenStore.setState({ token: null })
        const token = await useTokenStore.getState().getToken()
        return token.accessToken
      })().finally(() => {
        refreshPromise = null
      })
    }

    const accessToken = await refreshPromise
    config.headers.Authorization = `Bearer ${accessToken}`
    return await instance.request(config)
  } catch {
    return null
  }
}

export function attachResponseInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    async (rawError: unknown) => {
      if (axios.isCancel(rawError)) return Promise.reject(rawError)
      const error = rawError as AxiosError

      const config = error.config
      if (!config) {
        enrichError(error)
        return Promise.reject(error)
      }

      // 401: attempt a single token refresh + retry
      if (error.response?.status === 401) {
        const retryResponse = await handle401Retry(error, instance)
        if (retryResponse) return retryResponse
        enrichError(error)
        trackException(error, {
          url: config.url ?? 'unknown',
          method: config.method ?? 'unknown',
          status: '401',
        })
        return Promise.reject(error)
      }

      // General retry for transient failures
      if (shouldRetry(error, config)) {
        config._retryCount = (config._retryCount ?? 0) + 1
        const delay = getBackoffDelay(error, config._retryCount)
        await sleep(delay)
        return instance.request(config)
      }

      // Final failure — enrich and track
      enrichError(error)
      trackException(error, {
        url: config.url ?? 'unknown',
        method: config.method ?? 'unknown',
        status: String(error.response?.status ?? 'network'),
        technicalDetail: error.technicalDetail ?? '',
      })
      return Promise.reject(error)
    },
  )
}
